/**
 * What a user's draft says about Marvin's markups on one sheet: each shape's id and kept flag, read at the
 * card's exact draft address, and where each of a card's rows stands. Pure, so the web card
 * (`components/marvin/markup/`) and the native engine's markup commands (`native/engineCardCommands.ts`)
 * read a draft by one rule.
 *
 * `undefined`: not read yet. `"unknown"`: the draft could not be read at that address (the read failed, found
 * no draft there, or answered for another address): that is not a removal. Only a draft that was read and
 * lacks a markup means it was removed.
 */
export type DecisionShapes = { id: string; kept?: boolean }[];
export type SheetDecisionState = DecisionShapes | "unknown" | undefined;

export interface DraftAddress {
    fileId: string;
    page: number;
    /** Null only on a card saved before the revision was recorded: nothing to check the answer against. */
    revisionId: string | null;
}

export type DraftLike = {
    /** The draft's id and the server's version of it: each save of one draft raises its version. */
    id?: string;
    version?: number;
    revision_id?: string | null;
    shapes: readonly { id: string; kept?: boolean }[];
};

/** Which draft an answer came from, and at which version. */
export type DraftStamp = { id: string; version: number };

export function draftStamp(draft: DraftLike | null | undefined): DraftStamp | null {
    return draft && typeof draft.id === "string" && typeof draft.version === "number"
        ? { id: draft.id, version: draft.version }
        : null;
}

/**
 * Whether `incoming` is an older save of the draft `held` came from: the same draft at a lower version. Answers
 * about one draft can arrive out of order (a decision's saved draft overtaking another decision's, or a read's),
 * and an older one must not undo a newer one. Another draft, or an answer without a version, is not older.
 * The one ordering rule for the web card (`sheetDecisionState`) and native's cards (`engineCardCommands`).
 */
export function isOlderDraft(
    held: DraftStamp | null,
    incoming: DraftLike | null | undefined,
): boolean {
    const stamp = draftStamp(incoming);
    return held !== null && stamp !== null && stamp.id === held.id && stamp.version < held.version;
}

/** The decision state of a draft read at `address`; a missing draft, or one of another revision, is unknown. */
export function toDecisionShapes(
    draft: DraftLike | null | undefined,
    address: DraftAddress,
): SheetDecisionState {
    if (draft === undefined) return undefined;
    if (draft === null) return "unknown";
    if (address.revisionId && draft.revision_id !== address.revisionId) return "unknown";
    return draft.shapes.map((shape) => ({ id: shape.id, kept: shape.kept }));
}

/** The caller's own draft among the sets a page read returns (`MarkupApi.getVisible`), or null. */
export function ownDraft<T extends { status: string }>(sets: readonly T[]): T | null {
    return sets.find((set) => set.status === "draft") ?? null;
}

/**
 * Where a row stands in the user's draft: still to decide (pending), kept, removed, or not known (the draft could
 * not be read at the card's address). Only a draft that exists and lacks a markup means it was removed.
 */
export type MarkupRowState = "open" | "kept" | "removed" | "unknown";

/**
 * Each row's state from the user's own draft at the card's address. `undefined` (not read yet) leaves every row
 * pending: the server's Keep / Remove are harmless to repeat. `"unknown"` (the read failed, or found no draft or a
 * different address) leaves every row unknown, never removed. A draft that was read and lacks a row's shape
 * means that markup was removed.
 */
export function rowStates(
    rows: readonly { shapeId: string }[],
    draftShapes: readonly { id: string; kept?: boolean }[] | "unknown" | undefined,
): MarkupRowState[] {
    if (draftShapes === undefined) return rows.map(() => "open");
    if (draftShapes === "unknown") return rows.map(() => "unknown");
    const byId = new Map(draftShapes.map((shape) => [shape.id, shape]));
    return rows.map((row) => {
        const shape = byId.get(row.shapeId);
        if (!shape) return "removed";
        return shape.kept === true ? "kept" : "open";
    });
}
